import { FormEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  createCollectionTarget,
  deleteCollectionTarget,
  errorMessage,
  fetchCollectionBudget,
  fetchCollectionDemandClusters,
  fetchCollectionRuns,
  fetchCollectionSignals,
  fetchOsmStats,
  fetchZoneCensuses,
  runCollectionTarget,
  runCollectionDemandClusters,
  setCollectionTargetActive,
  suggestMarketAddresses,
  type AddressSuggestion,
  type CollectionFrequency,
  type CollectionRun,
  type CollectionRunStatus,
  type CollectionTarget,
  type CollectionTargetKind,
} from './api';

interface HistoricalCollectionProps {
  targets: CollectionTarget[];
  targetsState: 'pending' | 'error' | 'success';
  targetsError: unknown;
  onRetryTargets: () => void;
}

const dateTime = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const integer = new Intl.NumberFormat('it-IT');
const euro = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const TARGET_KIND_LABELS: Record<CollectionTargetKind, string> = {
  zona: 'Zona',
  demand_zone: 'Bisogni',
};
const FREQUENCY_LABELS: Record<CollectionFrequency, string> = {
  daily: 'Ogni giorno',
  weekly: 'Ogni settimana',
  monthly: 'Ogni mese',
  quarterly: 'Ogni 3 mesi',
};
const RUN_STATUS_LABELS: Record<CollectionRunStatus, string> = {
  running: 'In corso',
  ok: 'Completata',
  error: 'Errore',
  skipped_budget: 'Budget esaurito',
};
const RADII = [
  { value: 500, label: '500 m' },
  { value: 1_000, label: '1 km' },
  { value: 2_000, label: '2 km' },
  { value: 5_000, label: '5 km' },
];

function money(cents: number): string {
  return euro.format(cents / 100);
}

function targetDescription(target: CollectionTarget): string {
  if (target.kind === 'zona') {
    const radius = target.config.radiusMeters ?? 2_000;
    const radiusLabel = radius >= 1_000 ? `${radius / 1_000} km` : `${radius} m`;
    return `${target.config.address || 'Indirizzo non disponibile'} · raggio ${radiusLabel}`;
  }
  return [target.config.city, target.config.sector].filter(Boolean).join(' · ') || 'Città non disponibile';
}

function latestRunsByTarget(runs: CollectionRun[]): Map<string, CollectionRun> {
  const latest = new Map<string, CollectionRun>();
  runs.forEach((run) => {
    if (run.kind === 'zona-registro') return;
    if (!latest.has(run.targetId)) latest.set(run.targetId, run);
  });
  return latest;
}

function latestRegistryRunsByTarget(runs: CollectionRun[]): Map<string, CollectionRun> {
  const latest = new Map<string, CollectionRun>();
  runs.forEach((run) => {
    if (run.kind === 'zona-registro' && !latest.has(run.targetId)) latest.set(run.targetId, run);
  });
  return latest;
}

function CollectionTargetHistory({ target, onClose }: { target: CollectionTarget; onClose: () => void }) {
  const lat = target.config.lat;
  const lng = target.config.lng;
  const radiusMeters = target.config.radiusMeters ?? 2_000;
  const hasCoordinates = typeof lat === 'number'
    && typeof lng === 'number'
    && Number.isFinite(lat)
    && Number.isFinite(lng)
    && !(lat === 0 && lng === 0);
  const censuses = useQuery({
    queryKey: ['collection-censuses', target.id],
    queryFn: () => fetchZoneCensuses(target.id),
    enabled: target.kind === 'zona',
  });
  const signals = useInfiniteQuery({
    queryKey: ['collection-signals', target.id],
    queryFn: ({ pageParam }) => fetchCollectionSignals(target.id, pageParam, 50),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.last ? undefined : last.number + 1),
    enabled: target.kind === 'demand_zone',
  });
  const clusters = useQuery({
    queryKey: ['collection-demand-clusters', target.id],
    queryFn: () => fetchCollectionDemandClusters(target.id),
    enabled: target.kind === 'demand_zone',
  });
  const regenerateClusters = useMutation({
    mutationFn: () => runCollectionDemandClusters(target.id),
    onSuccess: () => clusters.refetch(),
  });
  const osm = useQuery({
    queryKey: ['collection-osm-stats', lat, lng, radiusMeters],
    queryFn: () => fetchOsmStats(lat!, lng!, radiusMeters),
    enabled: target.kind === 'zona' && hasCoordinates,
    staleTime: 24 * 60 * 60_000,
  });
  const signalRows = signals.data?.pages.flatMap((page) => page.content) ?? [];
  const latestClusterPeriod = clusters.data?.[0]?.period ?? null;
  const clusterRows = (clusters.data ?? []).filter((cluster) => cluster.period === latestClusterPeriod);
  const osmTotal = (osm.data ?? []).reduce((sum, row) => sum + row.count, 0);
  const osmCategoryCount = new Set((osm.data ?? []).map((row) => row.category)).size;

  return <section className="collection-target-history" aria-labelledby={`collection-history-${target.id}`}>
    <header><div><p className="eyebrow">Storico del target</p><h3 id={`collection-history-${target.id}`}>{target.label}</h3></div><button type="button" className="btn-quiet" onClick={onClose}>Chiudi</button></header>
    {target.kind === 'zona' && <>
      <div className="collection-osm-context">
        <span className="collection-osm-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="m4 6 5-2 6 2 5-2v14l-5 2-6-2-5 2Z"/><path d="M9 4v14m6-12v14"/></svg></span>
        <div><span>Vista OpenStreetMap</span>{!hasCoordinates ? <p>Disponibile dopo la prima raccolta, quando la zona avrà coordinate verificate.</p> : osm.isPending ? <p role="status">Controllo la copertura gratuita della zona…</p> : osm.isError ? <p>Copertura OSM temporaneamente non disponibile.</p> : <p><strong>{integer.format(osmTotal)} attività</strong> in {integer.format(osmCategoryCount)} {osmCategoryCount === 1 ? 'categoria' : 'categorie'}.</p>}</div>
        <small>Fonte OSM · senza rating</small>
      </div>
      {censuses.isPending && <div className="collection-detail-loading" role="status"><span className="sr-only">Caricamento censimenti</span><i/><i/><i/></div>}
      {censuses.isError && <div className="notice notice-error"><p>{errorMessage(censuses.error)}</p><button type="button" className="btn-secondary" onClick={() => censuses.refetch()}>Riprova</button></div>}
      {censuses.isSuccess && censuses.data.length === 0 && <div className="activity-empty"><strong>Nessun censimento disponibile</strong><p>Esegui il target per creare il primo punto storico della zona.</p></div>}
      {censuses.isSuccess && censuses.data.length > 0 && <ol className="collection-census-list">{censuses.data.map((census, index) => {
        const older = censuses.data[index + 1];
        const delta = older ? census.businessesFound - older.businessesFound : null;
        return <li key={census.id}>
          <time dateTime={census.capturedAt}>{dateTime.format(new Date(census.capturedAt))}</time>
          <dl><div><dt>Attività</dt><dd>{integer.format(census.businessesFound)}{delta != null && delta !== 0 && <small className={delta > 0 ? 'is-positive' : 'is-negative'}>{delta > 0 ? '+' : ''}{integer.format(delta)}</small>}</dd></div><div><dt>Popolazione</dt><dd>{census.population > 0 ? `~${integer.format(census.population)}` : 'n/d'}</dd></div><div><dt>Categorie</dt><dd>{integer.format(census.rows.length)}</dd></div></dl>
        </li>;
      })}</ol>}
    </>}
    {target.kind === 'demand_zone' && <>
      <section className="collection-demand-themes" aria-labelledby={`collection-themes-${target.id}`}>
        <header><div><p className="eyebrow">Sintesi dei segnali</p><h4 id={`collection-themes-${target.id}`}>Temi del mese</h4><p>{latestClusterPeriod ? `Periodo ${latestClusterPeriod}` : 'I segnali ricorrenti raggruppati per bisogno.'}</p></div><button type="button" className="btn-secondary" disabled={regenerateClusters.isPending} onClick={() => regenerateClusters.mutate()}>{regenerateClusters.isPending ? 'Rigenero…' : 'Rigenera · ~€0,01'}</button></header>
        {clusters.isPending && <div className="collection-detail-loading" role="status"><span className="sr-only">Caricamento temi del mese</span><i/><i/></div>}
        {clusters.isError && <div className="notice notice-error"><p>{errorMessage(clusters.error)}</p><button type="button" className="btn-secondary" onClick={() => clusters.refetch()}>Riprova</button></div>}
        {regenerateClusters.isError && <p className="error" role="alert">{errorMessage(regenerateClusters.error)}</p>}
        {clusters.isSuccess && clusterRows.length === 0 && <div className="collection-themes-empty"><strong>Nessun tema disponibile</strong><p>Rigenera dopo aver raccolto abbastanza segnali di bisogno.</p></div>}
        {clusterRows.length > 0 && <div className="collection-theme-list">{clusterRows.map((cluster) => <details key={cluster.id}><summary><span><strong>{cluster.clusterLabel}</strong><small>{integer.format(cluster.signalsCount)} {cluster.signalsCount === 1 ? 'segnale' : 'segnali'}</small></span><b aria-hidden="true">+</b></summary>{cluster.examples?.length ? <ul>{cluster.examples.map((example, index) => <li key={`${cluster.id}-${index}`}>{example}</li>)}</ul> : <p>Nessun esempio disponibile per questo tema.</p>}</details>)}</div>}
      </section>
      {signals.isPending && <div className="collection-detail-loading" role="status"><span className="sr-only">Caricamento segnali di bisogno</span><i/><i/><i/></div>}
      {signals.isError && <div className="notice notice-error"><p>{errorMessage(signals.error)}</p><button type="button" className="btn-secondary" onClick={() => signals.refetch()}>Riprova</button></div>}
      {signals.isSuccess && signalRows.length === 0 && <div className="activity-empty"><strong>Nessun segnale raccolto</strong><p>Esegui il target per interrogare le sonde di ricerca della città.</p></div>}
      {signalRows.length > 0 && <ol className="collection-signal-list">{signalRows.map((signal) => <li className={signal.seenCount >= 3 ? 'is-recurring' : undefined} key={signal.id}>
        <div><strong>{signal.signal}</strong>{signal.seenCount >= 3 && <span>Ricorrente</span>}</div>
        <p>Query sonda: {signal.querySeed || 'non disponibile'}</p>
        <footer><strong>Visto {integer.format(signal.seenCount)} {signal.seenCount === 1 ? 'volta' : 'volte'}</strong><span>Prima: <time dateTime={signal.firstSeenAt}>{dateTime.format(new Date(signal.firstSeenAt))}</time></span><span>Ultima: <time dateTime={signal.lastSeenAt}>{dateTime.format(new Date(signal.lastSeenAt))}</time></span></footer>
      </li>)}</ol>}
      {signals.hasNextPage && <button type="button" className="btn-more" disabled={signals.isFetchingNextPage} onClick={() => signals.fetchNextPage()}>{signals.isFetchingNextPage ? 'Carico…' : 'Carica altri segnali'}</button>}
    </>}
  </section>;
}

export default function HistoricalCollection({ targets, targetsState, targetsError, onRetryTargets }: HistoricalCollectionProps) {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [kind, setKind] = useState<CollectionTargetKind>('zona');
  const [customLabel, setCustomLabel] = useState('');
  const [frequency, setFrequency] = useState<CollectionFrequency>('monthly');
  const [address, setAddress] = useState('');
  const [radiusMeters, setRadiusMeters] = useState(2_000);
  const [city, setCity] = useState('');
  const [sector, setSector] = useState('');
  const [debouncedAddress, setDebouncedAddress] = useState('');
  const [addressPicked, setAddressPicked] = useState(false);
  const [addressMenuOpen, setAddressMenuOpen] = useState(false);
  const [activeAddressIndex, setActiveAddressIndex] = useState(-1);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);

  const budget = useQuery({ queryKey: ['collection-budget'], queryFn: fetchCollectionBudget });
  const runs = useQuery({ queryKey: ['collection-runs', 0, 100], queryFn: () => fetchCollectionRuns(0, 100) });
  const addressSuggestions = useQuery({
    queryKey: ['collection-address-suggestions', debouncedAddress],
    queryFn: ({ signal }) => suggestMarketAddresses(debouncedAddress, 6, signal),
    enabled: formOpen && kind === 'zona' && debouncedAddress.length >= 3,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const latestRuns = useMemo(() => latestRunsByTarget(runs.data?.content ?? []), [runs.data]);
  const latestRegistryRuns = useMemo(() => latestRegistryRunsByTarget(runs.data?.content ?? []), [runs.data]);
  const selectedTarget = targets.find((target) => target.id === selectedTargetId) ?? null;

  const refreshCollection = () => {
    queryClient.invalidateQueries({ queryKey: ['collection-targets'] });
    queryClient.invalidateQueries({ queryKey: ['collection-runs'] });
    queryClient.invalidateQueries({ queryKey: ['collection-budget'] });
  };
  const createTarget = useMutation({
    mutationFn: createCollectionTarget,
    onSuccess: () => {
      refreshCollection();
      setFormOpen(false);
      setCustomLabel('');
      setAddress('');
      setCity('');
      setSector('');
      setAddressPicked(false);
    },
  });
  const runTarget = useMutation({
    mutationFn: runCollectionTarget,
    onSuccess: (_, id) => {
      refreshCollection();
      queryClient.invalidateQueries({ queryKey: ['collection-censuses', id] });
      queryClient.invalidateQueries({ queryKey: ['collection-signals', id] });
      queryClient.invalidateQueries({ queryKey: ['collection-demand-clusters', id] });
    },
  });
  const toggleTarget = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setCollectionTargetActive(id, active),
    onSuccess: refreshCollection,
  });
  const removeTarget = useMutation({
    mutationFn: deleteCollectionTarget,
    onSuccess: (_, id) => {
      if (selectedTargetId === id) setSelectedTargetId(null);
      refreshCollection();
    },
  });

  useEffect(() => {
    const normalized = address.trim();
    if (!formOpen || kind !== 'zona' || addressPicked || normalized.length < 3) {
      setDebouncedAddress('');
      return;
    }
    const timer = window.setTimeout(() => setDebouncedAddress(normalized), 300);
    return () => window.clearTimeout(timer);
  }, [address, addressPicked, formOpen, kind]);

  useEffect(() => setActiveAddressIndex(-1), [addressSuggestions.data]);

  function chooseAddress(suggestion: AddressSuggestion) {
    setAddress(suggestion.label);
    setAddressPicked(true);
    setAddressMenuOpen(false);
    setActiveAddressIndex(-1);
  }

  function handleAddressKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const suggestions = addressSuggestions.data ?? [];
    const menuVisible = addressMenuOpen && !addressPicked && debouncedAddress === address.trim();
    if (!menuVisible) return;
    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setActiveAddressIndex((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setActiveAddressIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && activeAddressIndex >= 0 && suggestions[activeAddressIndex]) {
      event.preventDefault();
      chooseAddress(suggestions[activeAddressIndex]);
    } else if (event.key === 'Escape') {
      setAddressMenuOpen(false);
      setActiveAddressIndex(-1);
    }
  }

  function changeKind(next: CollectionTargetKind) {
    setKind(next);
    setFrequency(next === 'zona' ? 'monthly' : 'weekly');
    createTarget.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const normalizedAddress = address.trim();
    const normalizedCity = city.trim();
    const normalizedSector = sector.trim();
    if (kind === 'zona' && normalizedAddress.length < 3) return;
    if (kind === 'demand_zone' && normalizedCity.length < 2) return;
    const generatedLabel = kind === 'zona'
      ? `Zona: ${normalizedAddress}`
      : `Bisogni: ${[normalizedSector, normalizedCity].filter(Boolean).join(' · ')}`;
    createTarget.mutate({
      kind,
      label: customLabel.trim() || generatedLabel,
      frequency,
      config: kind === 'zona'
        ? { address: normalizedAddress, radiusMeters }
        : { city: normalizedCity, sector: normalizedSector || undefined },
    });
  }

  const capCents = budget.data ? budget.data.capCents ?? budget.data.placesBudgetCents : 0;
  const spentCents = budget.data?.spentCents ?? 0;
  const remainingCents = budget.data?.remainingCents ?? 0;
  const spentPercent = capCents > 0 ? Math.min(100, Math.round((spentCents / capCents) * 100)) : 0;
  const budgetLow = capCents > 0 && remainingCents / capCents < 0.2;
  const openapiCapCents = budget.data?.openapiBudgetCents ?? 0;
  const openapiSpentCents = budget.data?.openapiSpentCents ?? 0;
  const openapiRemainingCents = budget.data?.openapiRemainingCents ?? 0;
  const openapiSpentPercent = openapiCapCents > 0 ? Math.min(100, Math.round((openapiSpentCents / openapiCapCents) * 100)) : 0;
  const openapiBudgetLow = openapiCapCents > 0 && openapiRemainingCents / openapiCapCents < 0.2;
  const hasOpenapiBudget = budget.data?.openapiBudgetCents != null;
  const suggestions = addressSuggestions.data ?? [];
  const showAddressSuggestions = addressMenuOpen
    && !addressPicked
    && debouncedAddress === address.trim()
    && address.trim().length >= 3;
  const canCreate = kind === 'zona' ? address.trim().length >= 3 : city.trim().length >= 2;
  const actionError = createTarget.error ?? runTarget.error ?? toggleTarget.error ?? removeTarget.error;

  return <section className="activity-section collection-section" id="data-collection" aria-labelledby="collection-title">
    <div className="section-heading collection-section-heading"><div><h2 id="collection-title">Raccolta dati</h2><p>Accumula censimenti e segnali di bisogno per leggere come cambiano nel tempo.</p></div><div><span>{targetsState === 'success' ? targets.length : '…'}</span><button type="button" className="btn-secondary" aria-expanded={formOpen} aria-controls="collection-target-form" onClick={() => { setFormOpen((open) => !open); createTarget.reset(); }}>{formOpen ? 'Chiudi form' : 'Nuovo target'}</button></div></div>

    {budget.isPending && <div className="collection-budget-loading" role="status"><span className="sr-only">Caricamento budget raccolta</span><i/><i/></div>}
    {budget.isError && <div className="notice notice-error collection-budget-error"><p>{errorMessage(budget.error)}</p><button type="button" className="btn-secondary" onClick={() => budget.refetch()}>Riprova</button></div>}
    {budget.isSuccess && <div className="collection-budget-stack">
      <div className={budgetLow ? 'collection-budget is-low' : 'collection-budget'}>
        <div><span>Budget Places del mese</span><strong>{money(spentCents)} spesi su {money(capCents)}</strong></div>
        <progress max={Math.max(capCents, 1)} value={Math.min(spentCents, Math.max(capCents, 1))} aria-label={`Budget Places: ${money(spentCents)} spesi su ${money(capCents)}`}>{spentPercent}%</progress>
        <p><span>{spentPercent}% utilizzato</span><strong>{money(remainingCents)} disponibili</strong></p>
        {budget.data.placesFreeCallsMonthly != null && <small className="collection-budget-cap">Tetto effettivo comprensivo di {integer.format(budget.data.placesFreeCallsMonthly)} chiamate gratuite mensili.</small>}
        {budgetLow && <div className="collection-budget-warning" role="status">Residuo sotto il 20%: valuta la frequenza prima di aggiungere nuove zone.</div>}
      </div>
      {hasOpenapiBudget && <div className={`collection-budget is-openapi${openapiBudgetLow ? ' is-low' : ''}`}>
        <div><span>Registro/OMI (OpenAPI)</span><strong>{money(openapiSpentCents)} spesi su {money(openapiCapCents)}</strong></div>
        <progress max={Math.max(openapiCapCents, 1)} value={Math.min(openapiSpentCents, Math.max(openapiCapCents, 1))} aria-label={`Budget Registro e OMI: ${money(openapiSpentCents)} spesi su ${money(openapiCapCents)}`}>{openapiSpentPercent}%</progress>
        <p><span>{openapiSpentPercent}% utilizzato</span><strong>{money(openapiRemainingCents)} disponibili</strong></p>
        <small className="collection-budget-cap">Copre censimenti Registro Imprese e quotazioni OMI.</small>
        {openapiBudgetLow && <div className="collection-budget-warning" role="status">Residuo OpenAPI sotto il 20%: i censimenti ufficiali possono essere saltati.</div>}
      </div>}
    </div>}

    {formOpen && <form className="collection-target-form" id="collection-target-form" onSubmit={submit}>
      <fieldset className="collection-kind-picker"><legend>Che cosa vuoi seguire?</legend><label className={kind === 'zona' ? 'is-selected' : undefined}><input type="radio" name="collection-kind" value="zona" checked={kind === 'zona'} onChange={() => changeKind('zona')}/><strong>Zona</strong><small>Offerta commerciale e popolazione</small></label><label className={kind === 'demand_zone' ? 'is-selected' : undefined}><input type="radio" name="collection-kind" value="demand_zone" checked={kind === 'demand_zone'} onChange={() => changeKind('demand_zone')}/><strong>Bisogni</strong><small>Cosa viene cercato in una città</small></label></fieldset>
      <div className="collection-form-grid">
        <label className="field"><span>Nome <small>Opzionale</small></span><input type="text" maxLength={200} value={customLabel} onChange={(event) => setCustomLabel(event.target.value)} placeholder="Generato automaticamente dai dati"/></label>
        <label className="field"><span>Frequenza</span><select value={frequency} onChange={(event) => setFrequency(event.target.value as CollectionFrequency)}><option value="daily">Ogni giorno</option><option value="weekly">Ogni settimana</option><option value="monthly">Ogni mese</option><option value="quarterly">Ogni 3 mesi</option></select></label>
      </div>
      {kind === 'zona' ? <>
        <div className="field collection-address-field">
          <label htmlFor="collection-address">Indirizzo o zona</label>
          <div className="collection-address-control"><input id="collection-address" type="text" value={address} onChange={(event) => { setAddress(event.target.value); setAddressPicked(false); setAddressMenuOpen(true); }} onFocus={() => { if (!addressPicked) setAddressMenuOpen(true); }} onBlur={() => { setAddressMenuOpen(false); setActiveAddressIndex(-1); }} onKeyDown={handleAddressKeyDown} placeholder="Es. via Casilina 1300, Roma" autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={showAddressSuggestions} aria-controls={showAddressSuggestions ? 'collection-address-suggestions' : undefined} aria-activedescendant={showAddressSuggestions && activeAddressIndex >= 0 ? `collection-address-option-${activeAddressIndex}` : undefined}/>
            {showAddressSuggestions && <div className="collection-address-menu">{addressSuggestions.isFetching ? <p id="collection-address-suggestions" className="collection-address-status" role="status">Cerco indirizzi…</p> : addressSuggestions.isError ? <p id="collection-address-suggestions" className="collection-address-status">Suggerimenti non disponibili. Puoi inserire la zona manualmente.</p> : suggestions.length === 0 ? <p id="collection-address-suggestions" className="collection-address-status">Nessun indirizzo trovato.</p> : <ul id="collection-address-suggestions" role="listbox" aria-label="Indirizzi suggeriti">{suggestions.map((suggestion, index) => <li role="none" key={`${suggestion.label}-${index}`}><button id={`collection-address-option-${index}`} type="button" role="option" tabIndex={-1} aria-selected={activeAddressIndex === index} className={activeAddressIndex === index ? 'is-active' : undefined} onPointerDown={(event) => { event.preventDefault(); chooseAddress(suggestion); }} onClick={() => chooseAddress(suggestion)} onMouseEnter={() => setActiveAddressIndex(index)}><strong>{suggestion.main || suggestion.label}</strong>{suggestion.secondary && <small>{suggestion.secondary}</small>}</button></li>)}</ul>}</div>}
          </div>
          <small>Scegli un suggerimento quando disponibile; puoi anche inserire una zona manualmente.</small>
        </div>
        <fieldset className="collection-radius"><legend>Raggio</legend><div>{RADII.map((radius) => <label className={radiusMeters === radius.value ? 'is-selected' : undefined} key={radius.value}><input type="radio" name="collection-radius" value={radius.value} checked={radiusMeters === radius.value} onChange={() => setRadiusMeters(radius.value)}/><span>{radius.label}</span></label>)}</div></fieldset>
      </> : <div className="collection-form-grid"><label className="field"><span>Città</span><input type="text" value={city} onChange={(event) => setCity(event.target.value)} placeholder="Es. Roma"/></label><label className="field"><span>Settore <small>Opzionale</small></span><input type="text" value={sector} onChange={(event) => setSector(event.target.value)} placeholder="Es. pizzeria, palestra, retail"/></label></div>}
      <div className="collection-form-actions"><p>{kind === 'zona' ? 'Le zone usano Places e, quando disponibile, Registro/OMI: i due budget restano separati.' : 'Le sonde di bisogno usano Google Autocomplete e non incidono sul budget Places.'}</p><button type="submit" className="btn-primary" disabled={!canCreate || createTarget.isPending}>{createTarget.isPending ? 'Salvo…' : 'Salva target'}</button></div>
      {createTarget.isError && <p className="error" role="alert">{errorMessage(createTarget.error)}</p>}
    </form>}

    {targetsState === 'pending' && <div className="activity-loading" role="status"><span className="sr-only">Caricamento target raccolta</span>{[0, 1].map((item) => <div className="activity-skeleton-row" key={item} aria-hidden="true"><span/><span/></div>)}</div>}
    {targetsState === 'error' && <div className="notice notice-error"><p>{errorMessage(targetsError)}</p><button type="button" className="btn-secondary" onClick={onRetryTargets}>Riprova</button></div>}
    {targetsState === 'success' && targets.length === 0 && <div className="activity-empty"><strong>Nessun target storico</strong><p>Aggiungi una zona o una città per iniziare ad accumulare dati non ricostruibili in seguito.</p><button type="button" className="btn-secondary" onClick={() => setFormOpen(true)}>Aggiungi il primo target</button></div>}
    {targets.length > 0 && <ul className="collection-target-list">{targets.map((target) => {
      const latest = latestRuns.get(target.id);
      const latestRegistry = latestRegistryRuns.get(target.id);
      const activeTogglePending = toggleTarget.isPending && toggleTarget.variables?.id === target.id;
      return <li key={target.id}>
        <article className="collection-target-row">
          <div className="collection-target-main"><div><span className={target.kind === 'zona' ? 'collection-kind is-zone' : 'collection-kind is-demand'}>{TARGET_KIND_LABELS[target.kind]}</span><strong>{target.label}</strong></div><p>{targetDescription(target)} · {FREQUENCY_LABELS[target.frequency]}</p></div>
          <div className="collection-target-run"><span>Ultima raccolta</span>{latest ? <><strong className={`is-${latest.status}`}>{RUN_STATUS_LABELS[latest.status]}</strong><small>{integer.format(latest.items)} elementi · <time dateTime={latest.startedAt}>{dateTime.format(new Date(latest.startedAt))}</time></small></> : <><strong>{target.lastRunAt ? 'Eseguita' : 'Mai eseguita'}</strong>{target.lastRunAt && <small><time dateTime={target.lastRunAt}>{dateTime.format(new Date(target.lastRunAt))}</time></small>}</>}{target.kind === 'zona' && latestRegistry && <small className={`collection-registry-run is-${latestRegistry.status}`}>Registro: {RUN_STATUS_LABELS[latestRegistry.status]} · <time dateTime={latestRegistry.startedAt}>{dateTime.format(new Date(latestRegistry.startedAt))}</time></small>}</div>
          <button className={target.active ? 'monitor-toggle is-on' : 'monitor-toggle'} type="button" role="switch" aria-checked={target.active} aria-label={`${target.active ? 'Sospendi' : 'Riattiva'} ${target.label}`} disabled={activeTogglePending} onClick={() => toggleTarget.mutate({ id: target.id, active: !target.active })}><span/></button>
          <details className="row-menu"><summary aria-label={`Altre azioni per ${target.label}`}>•••</summary><div><button type="button" className="btn-danger" disabled={removeTarget.isPending && removeTarget.variables === target.id} onClick={() => { if (window.confirm(`Rimuovere «${target.label}»? I dati già raccolti resteranno conservati.`)) removeTarget.mutate(target.id); }}>{removeTarget.isPending && removeTarget.variables === target.id ? 'Rimuovo…' : 'Rimuovi target'}</button></div></details>
          <div className="collection-target-actions">{target.kind === 'zona' && <Link className="btn-quiet" to={`/storico/${encodeURIComponent(target.id)}`}>Dati nel tempo</Link>}<button type="button" className="btn-quiet" aria-expanded={selectedTargetId === target.id} onClick={() => setSelectedTargetId((current) => current === target.id ? null : target.id)}>{selectedTargetId === target.id ? 'Chiudi dati' : 'Dati tecnici'}</button><button type="button" className="btn-secondary" disabled={runTarget.isPending && runTarget.variables === target.id} onClick={() => runTarget.mutate(target.id)}>{runTarget.isPending && runTarget.variables === target.id ? 'Raccolgo…' : 'Esegui ora'}</button></div>
        </article>
        {selectedTarget?.id === target.id && <CollectionTargetHistory target={selectedTarget} onClose={() => setSelectedTargetId(null)}/>} 
      </li>;
    })}</ul>}
    {runs.isError && <p className="error">Lo storico delle ultime esecuzioni non è disponibile: {errorMessage(runs.error)}</p>}
    {actionError && !createTarget.isError && <p className="error" role="alert">{errorMessage(actionError)}</p>}
    <p className="collection-retention-note">I dati raccolti restano anche se rimuovi il target.</p>
  </section>;
}
